import React, { useState } from 'react';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { TextField, InputAdornment, IconButton, Popover } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const toYyyyMmDd = (v: string): string | null => {
  const s = String(v || '').trim();
  if (s.includes('/')) {
    const p = s.split('/').map((x) => parseInt(x, 10));
    if (p.length === 3 && !p.some(isNaN) && p[2] >= 1000) {
      return `${p[2]}-${String(p[0]).padStart(2, '0')}-${String(p[1]).padStart(2, '0')}`;
    }
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return null;
};

type Props = {
  control: Control<any>;
  errors: FieldErrors;
  handleFieldChange: (field: string, value: string) => void;
};

export const IncidentDateField: React.FC<Props> = ({ control, errors, handleFieldChange }) => {
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(null);

  return (
    <Controller
      name="incident_date"
      control={control}
      render={({ field }) => {
        const norm = field.value ? toYyyyMmDd(field.value) || field.value : '';
        const display = norm && /^\d{4}-\d{2}-\d{2}$/.test(norm)
          ? `${norm.slice(5, 7)}/${norm.slice(8, 10)}/${norm.slice(0, 4)}`
          : (field.value || '');
        const calVal = norm && /^\d{4}-\d{2}-\d{2}$/.test(norm)
          ? new Date(parseInt(norm.slice(0, 4), 10), parseInt(norm.slice(5, 7), 10) - 1, parseInt(norm.slice(8, 10), 10))
          : null;
        const setVal = (val: string) => {
          field.onChange(val);
          handleFieldChange('incident_date', val);
        };
        return (
          <>
            <TextField
              value={display}
              onChange={(e) => setVal(toYyyyMmDd(e.target.value) ?? e.target.value)}
              onBlur={(e) => {
                const v = (e.target as HTMLInputElement).value.trim();
                if (v) setVal(toYyyyMmDd(v) ?? v);
                field.onBlur();
              }}
              label="Incident Date (MM/DD/YYYY)"
              placeholder="MM/DD/YYYY"
              fullWidth
              error={!!errors.incident_date}
              helperText={errors.incident_date?.message ? String(errors.incident_date.message) : undefined}
              InputLabelProps={{ shrink: !!display }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="Calendar" onClick={(e) => setCalendarAnchor(e.currentTarget)} edge="end">
                      <CalendarMonthIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Popover
                open={!!calendarAnchor}
                anchorEl={calendarAnchor}
                onClose={() => setCalendarAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <DateCalendar
                  value={calVal}
                  onChange={(d) => {
                    if (d) {
                      setVal(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
                      setCalendarAnchor(null);
                    }
                  }}
                  maxDate={new Date()}
                />
              </Popover>
            </LocalizationProvider>
          </>
        );
      }}
    />
  );
};
